import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import Button from '../../../common/components/Button/Button';
import { CreateUpdateParamsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { ParamsForm } from './ParamsForm';

interface ParamsEditorProps {
  id: string;
  param: CreateUpdateParamsDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    param: CreateUpdateParamsDto,
    id: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  readOnly: boolean;
}

export const ParamsEditor: React.FC<ParamsEditorProps> = props => {
  const [param, setParam] = useState(props.param);
  const [show, setShow] = useState(props.show || false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  const saveParam = async () => {
    setIsLoading(true);
    props
      .onCompleted(param, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
        handleClose();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const createErrors = (errors: string[]) => {
    setSaveErrors(errors);
    setShowSaveErrors(!!errors.length);
  };

  const propertyChange = (obj: Partial<CreateUpdateParamsDto>) => {
    setParam({ ...param, ...obj });
  };

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setParam(props.param);
      setShow(true);
    }
  }, [props.param, props.show]);

  const handleClose = () => {
    setShow(false);
    if (props.handleClose) {
      props.handleClose();
    }
  };

  return (
    <Modal
      onHide={handleClose}
      show={show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(tk.administration.parameterEdit)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ParamsForm
          id={props.id}
          param={param}
          readOnly={props.readOnly}
          propertyChange={propertyChange}
          createErrors={createErrors}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        {!props.readOnly && (
          <Button variant="primary" onClick={saveParam} isLoading={isLoading}>
            {t(tk.button.save)}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
